import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './AttendanceQuestions.css';
import MessageSection from '../messageSection/MessageSection';
import { v4 as uuidv4 } from 'uuid';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AttendanceQuestions({ messageProps, questions, editorViewEnabled = false, templateKey }) {
  const { id: routeId } = useParams();
  const id = templateKey || routeId;
  const [sessionGuestId, setSessionGuestId] = useState(null);
  
  // Generate new ID on component mount
  useEffect(() => {
    setSessionGuestId(uuidv4());
  }, []);

  const [responses, setResponses] = useState({});
  const [isMessageVisible, setIsMessageVisible] = useState(false);

  const sendResponse = async (payload) => {
    const fullPayload = { ...payload, id, sessionGuestId, key: id };

    try {
      if (!editorViewEnabled) {
        await fetch(`${API_URL}/api/event-invites/response`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(fullPayload)
        });
      }
      setIsMessageVisible(true);
    } catch (error) {
      console.error('Failed to send response:', error);
    }
  };

  const handleQuestionResponse = async (questionId, response, parentId = null, isMultipleChoice = false, questionType) => {
    try {
      setResponses(prev => {
        if (isMultipleChoice) {
          const currentResponses = prev[questionId] || [];
          return {
            ...prev,
            [questionId]: currentResponses.includes(response)
              ? currentResponses.filter(r => r !== response)
              : [...currentResponses, response]
          };
        }
        return {
          ...prev,
          [questionId]: response.toLowerCase()
        };
      });

      const payload = isMultipleChoice
        ? {
            questionId,
            optionId: response,
            isSelected: !(responses[questionId] || []).includes(response),
            type: questionType
          }
        : {
            questionId,
            response: response.toLowerCase(),
            type: questionType,
            ...(parentId && { parentQuestionId: parentId })
          };

      await sendResponse(payload);
    } catch (error) {
      console.error('Failed to handle response:', error);
    }
  };

  const handleTextChange = (questionId, e) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: e.target.value
    }));
  };

  // Add a helper function to check if a section should be displayed
  const shouldDisplaySection = (dependencyRules) => {
    if (!dependencyRules) return true;

    return dependencyRules.every(rule => {
      const response = responses[rule.questionId];

      if ('expectedLength' in rule) {
        // Handle open-ended questions by checking the response length
        return response && typeof rule.expectedLength === 'number' && response.trim().length >= rule.expectedLength;
      }

      if ('expectedResponse' in rule) {
        // Handle fixed-response questions by checking the expected response
        return response && response.toLowerCase() === rule.expectedResponse.toLowerCase();
      }

      // If neither expectedLength nor expectedResponse is present, return false
      return false;
    });
  };

  const hasVisibleQuestionsAfter = (currentIndex) => {
    for (let i = currentIndex + 1; i < questions.length; i++) {
      if (shouldDisplaySection(questions[i].dependencies)) {
        return true;
      }
    }
    return false;
  };

  const renderQuestion = (question, index) => {
    const isVisible = shouldDisplaySection(question.dependencies);

    switch (question.type) {
      case 'question':
        return (
          <div className={`question-container ${isVisible ? 'visible' : ''}`}>
            <div className="transfer-section">
              <h3>{question.headerQuestion}</h3>
              <div className="button-options-centered">
                {question.options.map((option) => (
                  <button
                    key={option}
                    className={`attendance-button ${
                      responses[question.id] === option.toLowerCase() ? 'selected' : ''
                    }`}
                    onClick={() => handleQuestionResponse(question.id, option, null, false, question.type)}
                    type="button"
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'subQuestions':
        return (
          <div className={`question-container ${isVisible ? 'visible' : ''}`}>
            <h3>{question.headerQuestion}</h3>
            {question.subQuestions.map((subQ) => (
              <div key={subQ.id} className="question-group">
                <div className="question-row">
                  <h4>{subQ.headerQuestion}</h4>
                  <div className="button-options">
                    {subQ.options.map((option) => (
                      <button
                        key={option}
                        className={`attendance-button ${
                          responses[subQ.id] === option.toLowerCase() ? 'selected' : ''
                        }`}
                        onClick={() => handleQuestionResponse(subQ.id, option, question.id, false, question.type)}
                        type="button"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'multipleChoice':
        return (
          <div className={`question-container ${isVisible ? 'visible' : ''}`}>
            <div className="cocktail-section">
              <h3>{question.headerQuestion}</h3>
              <div className="cocktail-options">
                {question.options.map((option) => (
                  <div key={option.id} className="cocktail-item">
                    <label className="styled-checkbox">
                      <input
                        type="checkbox"
                        checked={(responses[question.id] || []).includes(option.id)}
                        onChange={() => handleQuestionResponse(question.id, option.id, null, true, question.type)}
                      />
                      <span className="checkbox-custom"></span>
                    </label>
                    <p className="cocktail-description">{option.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'open':
        return (
          <div className={`question-container ${isVisible ? 'visible' : ''}`}>
            <div className="dietary-section">
              <h3>{question.headerQuestion}</h3>
              <textarea
                className="dietary-input"
                value={responses[question.id] || ''}
                onChange={(e) => handleTextChange(question.id, e)}
                onBlur={() => handleQuestionResponse(question.id, responses[question.id] || '', null, false, question.type)}
                placeholder={question.placeholder}
                rows={question.rows}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="attendance-questions">
      <MessageSection {...messageProps}/>

      {questions.map((question, index) => (
        <React.Fragment key={question.id}>
          {renderQuestion(question, index)}
          {index < questions.length - 1 && (
            <div 
              className={`separator ${
                shouldDisplaySection(question.dependencies) && 
                hasVisibleQuestionsAfter(index) ? 'visible' : ''
              }`}
            />
          )}
        </React.Fragment>
      ))}

      <div className={`response-saved-message ${isMessageVisible ? 'visible' : ''}`}>
        Odpovědi se ukládají automaticky. <br/> Odesílání formuláře není nutné.
      </div>
    </div>
  );
}

export default AttendanceQuestions; 